import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings, LogOut, ChevronDown, Globe, Check, X, ShieldCheck, Home, LayoutDashboard, ChevronRight, Download, Loader2Icon, Eye, Headset } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Avatar } from '@brainsway/ui';
import { useAuth, useAuthStore, usePermissions } from '@/features/auth';
import { switchLanguage } from '@/utils/langTransition';
import { platform } from '@/utils/platform';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { TOPBAR_COLORS, TopBarIconButton } from '@/components/topbar';
import { BaseModal } from '@/components/modal/BaseModal';
import { DetailModal } from '@/components/modal';

interface UserMenuProps {
  /** Custom trigger renderer — when provided, replaces the default TopBarIconButton */
  renderTrigger?: (props: { onClick: () => void }) => React.ReactNode;
}

export function UserMenu({ renderTrigger }: UserMenuProps = {}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const user = useAuthStore((s) => s.user);
  const { canAccessAdmin, hasPermission } = usePermissions();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const isInAdmin = location.pathname.startsWith('/admin');

  // Language confirm modal state
  const [langConfirmOpen, setLangConfirmOpen] = useState(false);
  const [langConfirmPending, _setLangConfirmPending] = useState(false);
  const [pendingLang, setPendingLang] = useState<'en' | 'he'>('en');

  // Desktop dropdown state
  const [desktopOpen, setDesktopOpen] = useState(false);
  const desktopBtnRef = useRef<HTMLDivElement>(null);

  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close desktop on escape (mobile handled by BaseModal)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDesktopOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  if (!user) return null;

  const name = user.firstName && user.lastName
    ? `${user.firstName} ${user.lastName}`
    : user.email;

  const roleName = (() => {
    if (user.employmentRole) {
      return i18n.language === 'he' ? user.employmentRole.name : (user.employmentRole.nameEn || user.employmentRole.name);
    }
    if (user.roleRef) {
      return i18n.language === 'he' ? user.roleRef.name : (user.roleRef.nameEn || user.roleRef.name);
    }
    return '';
  })();

  const pendingLangName = pendingLang === 'en' ? 'English' : 'עברית';

  const handleLanguageChange = () => {
    setPendingLang(i18n.language === 'he' ? 'en' : 'he');
    setLangConfirmOpen(true);
  };

  const handleLangSwitch = async (persist: boolean) => {
    setLangConfirmOpen(false);
    // Let the modal close before switching language to avoid re-render flash
    await new Promise((r) => requestAnimationFrame(r));
    await switchLanguage(i18n, pendingLang, persist);
  };

  // Desktop dropdown position — auto-detect: top-half opens down, bottom-half opens up
  const getDropdownPosition = (): React.CSSProperties => {
    if (!desktopBtnRef.current) return {};
    const rect = desktopBtnRef.current.getBoundingClientRect();
    const dropdownWidth = 260;
    const left = Math.min(
      Math.max(8, rect.left),
      window.innerWidth - dropdownWidth - 8,
    );
    const openUp = rect.top > window.innerHeight / 2;
    return {
      position: 'fixed',
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 8 }
        : { top: rect.bottom + 8 }),
      left,
      width: dropdownWidth,
      zIndex: 9999,
    };
  };

  // Mobile menu item component
  const MobileMenuItem = ({
    icon: Icon,
    label,
    onClick,
    disabled = false,
  }: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    onClick: () => void;
    disabled?: boolean;
  }) => (
    <button
      onClick={() => {
        onClick();
        setMobileOpen(false);
      }}
      disabled={disabled}
      className="dock-icon-glass flex w-full items-center gap-3 px-4 py-3 text-sm font-bold uppercase transition-all active:scale-[0.98]"
      style={{
        borderRadius: 16,
        color: '#1a4fa0',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
    </button>
  );

  return (
    <>
      {/* === DESKTOP: Avatar button + Portal dropdown === */}
      {!isMobile && (
        <div ref={desktopBtnRef} data-testid="user-menu">
          {renderTrigger ? (
            renderTrigger({ onClick: () => setDesktopOpen(!desktopOpen) })
          ) : (
            <TopBarIconButton
              onClick={() => setDesktopOpen(!desktopOpen)}
              aria-label={t('nav.settings')}
              overflow
              active={desktopOpen}
              badge={
                <div
                  className="pointer-events-none absolute bottom-0 flex h-[18px] w-[18px] items-center justify-center rounded-full"
                  style={{
                    right: '0px',
                    backgroundColor: TOPBAR_COLORS.badge,
                    border: `1px solid ${TOPBAR_COLORS.border}`,
                  }}
                >
                  <ChevronDown className="h-3 w-3" style={{ color: TOPBAR_COLORS.text }} />
                </div>
              }
            >
              <Avatar src={user.avatarUrl} name={name} size="md" className="!ring-0 !shadow-none !h-full !w-full" />
            </TopBarIconButton>
          )}
        </div>
      )}

      {/* Desktop dropdown portal */}
      {!isMobile && createPortal(
        <AnimatePresence>
          {desktopOpen && (() => {
            const pos = getDropdownPosition();
            if (!pos.position) return null;
            const slideY = pos.bottom !== undefined ? 8 : -8;
            const subMenuLeft = (pos.left as number ?? 0) > window.innerWidth / 2;
            return (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-[9998]"
                  onClick={() => setDesktopOpen(false)}
                />
                <motion.div
                  initial={{ opacity: 0, y: slideY }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: slideY }}
                  transition={{ duration: 0.15 }}
                  className="fixed z-[9999] rounded-xl bg-white shadow-lg ring-1 ring-gray-200"
                  dir="ltr"
                  style={pos}
                  role="menu"
                  aria-label={t('nav.settings')}
                >
                  {/* Email header */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="truncate text-[13px] text-gray-500">{user.email}</p>
                    {roleName && (
                      <span className="mt-1 inline-block rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-600">
                        {roleName}
                      </span>
                    )}
                  </div>

                  {/* Section 1: Settings + Language */}
                  <div className="py-1">
                    <button
                      onClick={() => { navigate('/settings'); setDesktopOpen(false); }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors"
                      role="menuitem"
                    >
                      <Settings size={18} className="text-gray-400" />
                      <span className="flex-1 text-start">{t('nav.settings')}</span>
                    </button>
                    {platform.isWeb && (
                      <button
                        onClick={() => { navigate('/downloads'); setDesktopOpen(false); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors"
                        role="menuitem"
                      >
                        <Download size={18} className="text-gray-400" />
                        <span className="flex-1 text-start">{t('downloads.title')}</span>
                      </button>
                    )}
                    {/* Language with hover submenu */}
                    <div className="relative group">
                      <button
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors group-hover:bg-gray-50"
                        role="menuitem"
                      >
                        <Globe size={18} className="text-gray-400" />
                        <span className="flex-1 text-start">{t('common.toggleLanguage')}</span>
                        <ChevronRight size={14} className={`text-gray-300 ${subMenuLeft ? 'rotate-180' : ''}`} />
                      </button>
                      {/* Submenu — opens right when room, left when near right edge */}
                      <div className={`invisible group-hover:visible absolute top-0 w-52 rounded-xl bg-white shadow-lg ring-1 ring-gray-200 py-1 z-[10000] before:content-[''] before:absolute before:top-0 before:bottom-0 before:w-3 ${subMenuLeft ? 'end-full me-1 before:-end-3' : 'start-full ms-1 before:-start-3'}`}>
                        <button
                          onClick={() => { if (i18n.language !== 'en') handleLanguageChange(); setDesktopOpen(false); }}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors"
                          role="menuitem"
                        >
                          <span className="flex-1 text-start">English</span>
                          {i18n.language === 'en' && <Check size={16} className="text-gray-700" />}
                        </button>
                        <button
                          onClick={() => { if (i18n.language !== 'he') handleLanguageChange(); setDesktopOpen(false); }}
                          className="flex w-full items-center gap-3 px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors"
                          role="menuitem"
                        >
                          <span className="flex-1 text-start">עברית</span>
                          {i18n.language === 'he' && <Check size={16} className="text-gray-700" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100" />

                  {/* Section 2: Dashboard + Admin */}
                  <div className="py-1">
                    <button
                      onClick={() => { navigate('/dashboard'); setDesktopOpen(false); }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors"
                      role="menuitem"
                    >
                      <LayoutDashboard size={18} className="text-gray-400" />
                      <span className="flex-1 text-start">{t('nav.dashboard')}</span>
                    </button>
                    {canAccessAdmin() && (
                      <button
                        onClick={() => { navigate(isInAdmin ? '/feed' : '/admin'); setDesktopOpen(false); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors"
                        role="menuitem"
                      >
                        {isInAdmin ? <Home size={18} className="text-gray-400" /> : <ShieldCheck size={18} className="text-gray-400" />}
                        <span className="flex-1 text-start">{isInAdmin ? t('nav.switchToHome') : t('nav.switchToAdmin')}</span>
                      </button>
                    )}
                    {hasPermission('tab:user_impersonation') && (
                      <button
                        onClick={() => { navigate('/admin/user-impersonation'); setDesktopOpen(false); }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors"
                        role="menuitem"
                      >
                        <Eye size={18} className="text-gray-400" />
                        <span className="flex-1 text-start">{t('admin.impersonation.viewAs')}</span>
                      </button>
                    )}
                  </div>

                  {/* Support */}
                  <div className="py-1">
                    <button
                      onClick={() => { navigate('/support'); setDesktopOpen(false); }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors"
                      role="menuitem"
                    >
                      <Headset size={18} className="text-gray-400" />
                      <span className="flex-1 text-start">{t('support.title')}</span>
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100" />

                  {/* Logout */}
                  <div className="py-1">
                    <button
                      onClick={() => { logout(); setDesktopOpen(false); }}
                      data-testid="logout-button"
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors"
                      role="menuitem"
                    >
                      <LogOut size={18} className="text-gray-400" />
                      <span className="flex-1 text-start">{t('auth.logout')}</span>
                    </button>
                  </div>
                </motion.div>
              </>
            );
          })()}
        </AnimatePresence>,
        document.body
      )}

      {/* === MOBILE: Avatar button that opens fullscreen BaseModal === */}
      {isMobile && (
        renderTrigger ? (
          renderTrigger({ onClick: () => setMobileOpen(true) })
        ) : (
          <TopBarIconButton
            onClick={() => setMobileOpen(true)}
            aria-label={t('common.userMenu')}
            overflow
            active={mobileOpen}
            data-testid="user-menu-mobile"
            badge={
              <div
                className="pointer-events-none absolute bottom-0 flex h-[18px] w-[18px] items-center justify-center rounded-full"
                style={{
                  right: '0px',
                  backgroundColor: TOPBAR_COLORS.badge,
                  border: `1px solid ${TOPBAR_COLORS.border}`,
                }}
              >
                <ChevronDown className="h-3 w-3" style={{ color: TOPBAR_COLORS.text }} />
              </div>
            }
          >
            <Avatar src={user.avatarUrl} name={name} size="md" className="!ring-0 !shadow-none !h-full !w-full" />
          </TopBarIconButton>
        )
      )}

      {/* Mobile fullscreen modal — uses BaseModal for portal, backdrop, drag-dismiss, springs, ESC, scroll-lock */}
      <BaseModal
        isOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        mobileVariant="fullscreen"
        ariaLabel={t('common.userMenu')}
        flushTop
      >
        {/* Profile Section — frosted glass */}
        <div
          className="flex flex-col items-center px-4 pb-4 flex-shrink-0"
          style={{
            backgroundColor: 'rgba(255,255,255,0.55)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            borderBottom: '2px solid rgba(255,255,255,0.25)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            paddingTop: 'env(safe-area-inset-top, 0px)',
          }}
        >
          {/* Drag Handle + Close */}
          <div className="flex items-center justify-between w-full pt-2 pb-1">
            <div className="flex-1" />
            <div
              className="w-12 h-1.5 rounded-full"
              style={{ backgroundColor: 'rgba(0,0,0,0.15)' }}
            />
            <div className="flex-1 flex justify-end">
              <button
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-center rounded-full transition-all active:translate-y-0.5"
                style={{
                  width: 32,
                  height: 32,
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  border: '1px solid rgba(0,0,0,0.08)',
                }}
                aria-label={t('common.closeMenu')}
              >
                <X className="h-4 w-4" style={{ color: '#1e293b' }} />
              </button>
            </div>
          </div>

          {/* Large Avatar */}
          <div className="mb-3">
            <div
              className="h-20 w-20 overflow-hidden rounded-full"
              style={{ border: '1px solid rgba(0,0,0,0.1)' }}
            >
              <Avatar src={user.avatarUrl} name={name} size="lg" className="!ring-0 !shadow-none !h-full !w-full !rounded-full" />
            </div>
          </div>
          <h2 className="text-lg font-bold uppercase text-center" style={{ color: '#1e293b' }}>
            {name}
          </h2>
          <p className="text-sm mt-1 text-center" style={{ color: 'rgba(30,41,59,0.5)' }}>
            {user.email}
          </p>
          {roleName && (
            <span
              className="mt-2 inline-block rounded-full px-3 py-0.5 text-xs font-bold uppercase"
              style={{
                backgroundColor: 'rgba(0,0,0,0.06)',
                border: '0.5px solid rgba(0,0,0,0.1)',
                color: '#1e293b',
              }}
            >
              {roleName}
            </span>
          )}
        </div>

        {/* Menu Items — scrollable area */}
        <div
          className="flex-1 p-4 space-y-2 overflow-y-auto hide-scrollbar"
          style={{
            overscrollBehaviorY: 'contain',
            backgroundColor: 'rgba(255,255,255,0.55)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          {/* Language Toggle — full-width, mobile sizing */}
          <button
            type="button"
            dir="ltr"
            onClick={handleLanguageChange}
            disabled={langConfirmPending}
            className="relative flex items-center rounded-xl overflow-hidden w-full"
            style={{
              height: '40px',
              background: 'linear-gradient(90deg, #1a4fa0 0%, #3b8dd4 100%)',
              border: '0.5px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
              opacity: langConfirmPending ? 0.6 : 1,
            }}
            aria-label={t('common.toggleLanguage')}
          >
            <motion.div
              className="absolute top-0 bottom-0 rounded-lg"
              style={{
                width: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.25)',
                border: '0.5px solid rgba(255, 255, 255, 0.3)',
                margin: '2px',
              }}
              animate={{
                x: i18n.language === 'en' ? 0 : 'calc(100% - 4px)',
              }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
            <span
              className="relative z-10 w-1/2 text-center text-sm font-bold"
              style={{ color: '#ffffff', opacity: i18n.language === 'en' ? 1 : 0.4 }}
            >
              English
            </span>
            <span
              className="relative z-10 w-1/2 text-center text-sm font-bold"
              style={{ color: '#ffffff', opacity: i18n.language === 'he' ? 1 : 0.4 }}
            >
              עברית
            </span>
          </button>

          {/* Dashboard */}
          <MobileMenuItem
            icon={LayoutDashboard}
            label={t('nav.dashboard')}
            onClick={() => navigate('/dashboard')}
          />

          {/* Settings */}
          <MobileMenuItem
            icon={Settings}
            label={t('nav.settings')}
            onClick={() => navigate('/settings')}
          />

          {/* Downloads — web only (native users already have the app) */}
          {platform.isWeb && (
            <MobileMenuItem
              icon={Download}
              label={t('downloads.title')}
              onClick={() => navigate('/downloads')}
            />
          )}

          {/* Switch to Admin / Switch to Home */}
          {canAccessAdmin() && (
            <MobileMenuItem
              icon={isInAdmin ? Home : ShieldCheck}
              label={isInAdmin ? t('nav.switchToHome') : t('nav.switchToAdmin')}
              onClick={() => navigate(isInAdmin ? '/home' : '/admin')}
            />
          )}

          {/* View As (User Impersonation) */}
          {hasPermission('tab:user_impersonation') && (
            <MobileMenuItem
              icon={Eye}
              label={t('admin.impersonation.viewAs')}
              onClick={() => navigate('/admin/user-impersonation')}
            />
          )}
          {/* Support */}
          <MobileMenuItem
            icon={Headset}
            label={t('support.title')}
            onClick={() => navigate('/support')}
          />
        </div>

        {/* Logout Section — fixed at bottom */}
        <div
          className="flex-shrink-0 p-4"
          style={{
            paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))',
            backgroundColor: 'rgba(255,255,255,0.55)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          <button
            onClick={() => { logout(); setMobileOpen(false); }}
            data-testid="logout-button-mobile"
            className="dock-icon-glass flex w-full items-center justify-center gap-3 px-4 py-3 text-sm font-bold uppercase transition-all active:scale-[0.98]"
            style={{
              borderRadius: 16,
              color: '#1a4fa0',
            }}
          >
            <LogOut className="h-5 w-5" />
            <span>{t('auth.logout')}</span>
          </button>
        </div>
      </BaseModal>

      {/* Language confirm modal — DetailModal (fullscreen mobile like NotificationBell) */}
      <DetailModal
        isOpen={langConfirmOpen}
        onClose={() => setLangConfirmOpen(false)}
        preventClose={langConfirmPending}
        maxWidth="max-w-sm"
        ariaLabel={t('common.saveLanguageTitle')}
      >
        <div className="flex flex-col flex-1 min-h-0">
          {/* Header — matches NotificationBell mobile header */}
          <div
            className="flex items-center justify-between p-4 flex-shrink-0 bg-white"
            style={{ borderBottom: '1px solid #E4E6EB' }}
          >
            <h3 className="text-2xl font-extrabold" style={{ color: '#050505' }}>
              {t('common.saveLanguageTitle')}
            </h3>
            <button
              onClick={() => setLangConfirmOpen(false)}
              disabled={langConfirmPending}
              className="p-2 rounded-full transition-colors hover:bg-black/5 disabled:opacity-50"
              style={{ color: '#65676B' }}
              aria-label={t('common.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body — card + buttons grouped near top, like notification items */}
          <div className="p-3">
            {/* Message card — styled like a notification item */}
            <div
              className="flex items-center gap-3 p-4 mb-3 rounded-xl"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #E4E6EB',
              }}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base bg-blue-50">
                🌐
              </span>
              <p className="text-[15px] font-bold leading-tight" style={{ color: '#050505' }}>
                {t('common.saveLanguageMessage', { language: pendingLangName })}
              </p>
            </div>

            {/* Action buttons — right below the card */}
            <div className="flex gap-2">
              <button
                onClick={() => handleLangSwitch(true)}
                disabled={langConfirmPending}
                className="flex-1 py-2.5 rounded-lg text-center text-sm font-bold uppercase transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                style={{
                  color: '#FFFFFF',
                  backgroundColor: '#1877F2',
                }}
              >
                {langConfirmPending && <Loader2Icon className="h-4 w-4 animate-spin" />}
                {t('common.saveLanguageConfirm')}
              </button>
              <button
                onClick={() => handleLangSwitch(false)}
                disabled={langConfirmPending}
                className="flex-1 py-2.5 rounded-lg text-center text-sm font-bold uppercase transition-all active:scale-[0.98] disabled:opacity-50"
                style={{
                  color: '#65676B',
                  backgroundColor: '#F0F2F5',
                }}
              >
                {t('common.saveLanguageCancel')}
              </button>
            </div>
          </div>
        </div>
      </DetailModal>
    </>
  );
}
